import React, { useCallback, useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTheme } from "@mui/material/styles";

import { Subtitle, Title } from "components/elements/texts";
import CustomCard from "components/modules/cards";
import MasterPage from "components/modules/masterpage";
import { QuestionDetail } from "./components";
import ProgressBar from "../ExamsAvailable/components/ColoredProgressBar";
import { clearQuestionState, updateQuestion } from "state/slices/questionSlice";
import { cleanAnswerCode } from "state/slices/answerCodeSlice";
import { useExamAnswers } from "../../api/exam/useExamAnswers";
import EmptyState from "../../components/modules/emptyState";

const ExamQuestion = () => {
  const { exam_instance_id } = useParams();
  const dispatch = useDispatch();
  const currentQuestion = useSelector((state) => state.question);
  const answerCode = useSelector((state) => state.answerCode);
  const [loading, setLoading] = useState(false);
  const [examFinished, setExamFinished] = useState(false);
  const [openTimeoutCard, setOpenTimeoutCard] = useState(false);
  const [openFinalCard, setOpenFinalCard] = useState(false);
  const [openNoOptionSelectedCard, setOpenNoOptionSelectedCard] =
    useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [openCodeConfirmationModal, setOpenCodeConfirmationModal] =
    useState(false);

  const [timerKey, setTimerKey] = useState(0);
  const [timerDuration, setTimerDuration] = useState(0);
  const [timerUpdated, setTimerUpdated] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [questionType, setQuestionType] = useState();
  // True while the timeout auto-submit is in flight; Continue Now waits for it.
  const [timeoutSubmitPending, setTimeoutSubmitPending] = useState(false);
  const [timeoutSubmitFailed, setTimeoutSubmitFailed] = useState(false);
  // True while Continue Now's advance/submit request is in flight.
  const [continueNowPending, setContinueNowPending] = useState(false);

  const ubaToken = useSelector((state) => state.ubaToken.ubaToken);

  // Initialize exam answers API hook
  const { submitAnswer, loadInitialQuestion } = useExamAnswers(
    exam_instance_id,
    ubaToken?.tokenFromUrl
  );

  const myRef = useRef(null);
  // Holds the post-timeout API result so Continue Now can advance without
  // re-submitting the already-answered question (backend rejects double submit).
  const timeoutSubmitStateRef = useRef({
    status: "idle", // idle | pending | submitted | failed
    examRequestToken: null,
    finishedExam: false,
  });
  // Sync guard so a second Continue Now click in the same tick cannot start
  // another advance/submit before React re-renders.
  const continueNowInFlightRef = useRef(false);

  const scrollToTop = () => {
    myRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const theme = useTheme();

  let location = useLocation();
  useEffect(() => {}, [location]);

  const renderTimer = ({ remainingTime }) => {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    let newTimerColor = "black";
    if (minutes === 0 && seconds <= 10 && seconds % 2 === 0) {
      newTimerColor = "red";
    }
    if (minutes === 0) {
      return <div style={{ color: `${newTimerColor}` }}>{seconds}</div>;
    }
    if (seconds < 10) {
      return `${minutes}:0${seconds}`;
    }
    return `${minutes}:${seconds}`;
  };

  const shouldShowCodeConfirmation = useCallback(() => {
    if (currentQuestion?.question?.type !== "coding") return false;

    const hasWrittenCode = answerCode.user_code.length > 0;
    const codeModifiedSinceRun = answerCode.code_modified_since_run;
    const hasTimeRemaining = timerDuration >= 10;
    const lastRunTime = answerCode.last_code_run_time;

    // Show confirmation if:
    // 1. User has written code
    // 2. Code was modified since last run OR never been run
    // 3. Timer has 10+ seconds remaining
    return (
      hasWrittenCode &&
      (codeModifiedSinceRun || !lastRunTime) &&
      hasTimeRemaining
    );
  }, [currentQuestion, answerCode, timerDuration]);

  const answerQuestion = useCallback(
    async (
      fetchNextQuestion = true,
      {
        advanceOnly = false,
        examRequestToken = null,
        softError = false,
        suppressLoading = false,
      } = {}
    ) => {
      setIsPlaying(false);
      // Timeout auto-submit keeps the dialog mounted; other calls use full-page loading.
      if (!suppressLoading) {
        setLoading(true);
      }

      const answerData = {
        hash_id: advanceOnly
          ? null
          : currentQuestion
          ? currentQuestion.hash_id
          : null,
        exam_request_token:
          examRequestToken ??
          (currentQuestion ? currentQuestion.exam_request_token : null),
        fetch_next_question: fetchNextQuestion,
      };

      if (advanceOnly) {
        // After a timeout submit (fetch_next_question=false), the previous
        // question is already answered. Advance with the rotated token and no
        // answer payload so the backend does not try to register it again.
        answerData.selected_choices_ids = [];
        answerData.open_response = "";
        answerData.response_language = "python";
      } else if (currentQuestion?.question?.type === "coding") {
        answerData.open_response = answerCode.user_code;
        answerData.response_language =
          answerCode.language.language.toLowerCase();
      } else {
        answerData.selected_choices_ids = selectedOptions;
      }

      try {
        const response = currentQuestion
          ? await submitAnswer(answerData)
          : await loadInitialQuestion();

        if (response.error) {
          setLoading(false);
          setIsPlaying(false);
          if (!softError) {
            setShowEmptyState(true);
            dispatch(clearQuestionState());
          }
          return { error: response.error };
        }

        const data = response.data;
        setTimeout(scrollToTop, 500);
        setSelectedOptions([]);

        if (data.finished_exam) {
          setExamFinished(true);
        }

        if (fetchNextQuestion) {
          // Advancing (or finishing via an advance call): replace question state.
          dispatch(cleanAnswerCode());
          dispatch(updateQuestion(data));
          if (data?.question?.type) {
            const formattedString = data.question.type
              .replace(/_/g, " ")
              .replace(/(?:^|\s)\S/g, (match) => match.toUpperCase());
            setQuestionType(formattedString);
          }
        } else if (data.finished_exam) {
          // Last question timed out: exam is finished server-side, but keep the
          // current question UI under the timeout dialog until the user chooses.
          setExamFinished(true);
        }
        // else: timeout submit mid-exam — caller persists the new token in a ref.
        // Do not updateQuestion here; that would restart the timer on this question.

        setShowEmptyState(false);
        setLoading(false);
        return { data };
      } catch (error) {
        // This shouldn't happen as errors are handled in the API hook
        // but included as a safety net
        console.error("Unexpected error in answerQuestion:", error);
        setLoading(false);
        setIsPlaying(false);
        if (!softError) {
          setShowEmptyState(true);
          dispatch(clearQuestionState());
        }
        return { error };
      }
    },
    [
      currentQuestion,
      selectedOptions,
      answerCode,
      submitAnswer,
      loadInitialQuestion,
      dispatch,
    ]
  );

  const handleQuestionTimeout = useCallback(async () => {
    timeoutSubmitStateRef.current = {
      status: "pending",
      examRequestToken: null,
      finishedExam: false,
    };
    setTimeoutSubmitPending(true);
    setTimeoutSubmitFailed(false);

    // softError + suppressLoading: keep the timeout dialog mounted/usable.
    const result = await answerQuestion(false, {
      softError: true,
      suppressLoading: true,
    });

    if (result?.error) {
      timeoutSubmitStateRef.current = {
        status: "failed",
        examRequestToken: null,
        finishedExam: false,
      };
      setTimeoutSubmitFailed(true);
      setTimeoutSubmitPending(false);
      return;
    }

    timeoutSubmitStateRef.current = {
      status: "submitted",
      examRequestToken: result.data?.exam_request_token ?? null,
      finishedExam: !!result.data?.finished_exam,
    };
    setTimeoutSubmitFailed(false);
    setTimeoutSubmitPending(false);
  }, [answerQuestion]);

  const handleContinueNowAfterTimeout = useCallback(async () => {
    if (continueNowInFlightRef.current) {
      return;
    }

    const timeoutState = timeoutSubmitStateRef.current;

    // Wait for the timeout auto-submit to finish before continuing.
    if (timeoutState.status === "pending" || timeoutSubmitPending) {
      return;
    }

    continueNowInFlightRef.current = true;
    setContinueNowPending(true);
    setOpenTimeoutCard(false);

    try {
      if (timeoutState.status === "submitted" && timeoutState.finishedExam) {
        setOpenFinalCard(true);
        timeoutSubmitStateRef.current = {
          status: "idle",
          examRequestToken: null,
          finishedExam: false,
        };
        return;
      }

      let result;
      if (timeoutState.status === "submitted") {
        // Advance only: previous answer was already registered on timeout.
        // Re-submitting the same hash_id/token would raise a backend ValueError.
        result = await answerQuestion(true, {
          advanceOnly: true,
          examRequestToken: timeoutState.examRequestToken,
          softError: true,
        });
      } else {
        // Timeout auto-submit failed or never completed: submit + fetch next once.
        result = await answerQuestion(true, { softError: true });
      }

      if (result?.error) {
        // Keep resume options available instead of dropping into EmptyState.
        setTimeoutSubmitFailed(true);
        setOpenTimeoutCard(true);
        return;
      }

      setTimeoutSubmitFailed(false);
      timeoutSubmitStateRef.current = {
        status: "idle",
        examRequestToken: null,
        finishedExam: false,
      };
    } finally {
      continueNowInFlightRef.current = false;
      setContinueNowPending(false);
    }
  }, [answerQuestion, timeoutSubmitPending]);

  useEffect(() => {
    setTimerKey((prevKey) => prevKey + 1);
  }, [timerUpdated]);

  useEffect(() => {
    if (currentQuestion !== null) {
      setTimerDuration(currentQuestion.question.time);
      setIsPlaying(true);
    }
  }, [timerKey]);

  useEffect(() => {
    if (currentQuestion !== null && !currentQuestion.finished_exam) {
      setTimerUpdated((prevState) => !prevState);
    }

    currentQuestion && currentQuestion.finished_exam && setOpenFinalCard(true);
  }, [currentQuestion]);

  const sendAnswer = () => {
    // Check if user should see code confirmation modal
    if (shouldShowCodeConfirmation()) {
      setOpenCodeConfirmationModal(true);
      return;
    }

    // Existing validation logic
    if (
      (selectedOptions !== null && selectedOptions.length > 0) ||
      answerCode.user_code.length > 0
    ) {
      if (currentQuestion.question_number === currentQuestion.exam_length) {
        setOpenFinalCard(true);
        setExamFinished(true);
      }
      answerQuestion();
    } else {
      setOpenNoOptionSelectedCard(true);
    }
  };

  const handleConfirmSubmitCode = () => {
    setOpenCodeConfirmationModal(false);
    if (currentQuestion.question_number === currentQuestion.exam_length) {
      setOpenFinalCard(true);
      setExamFinished(true);
    }
    answerQuestion();
  };

  useEffect(() => {
    openFinalCard && dispatch(clearQuestionState());
  }, [openFinalCard]);

  const selectOption = (id, type) => {
    if (type === "radio") {
      setSelectedOptions([id]);
    } else if (type === "checkbox") {
      if (selectedOptions.includes(id)) {
        var newSelectedOptions = [...selectedOptions];
        newSelectedOptions.splice(newSelectedOptions.indexOf(id), 1);
        setSelectedOptions(newSelectedOptions);
      } else {
        setSelectedOptions((current) => [...current, id]);
      }
    }
  };

  useEffect(() => {
    !currentQuestion && !examFinished && answerQuestion();
  }, [currentQuestion]);

  const navigate = useNavigate();

  const routeChange = (route) => {
    setOpenFinalCard(false);
    navigate(route);
  };

  return (
    <MasterPage>
      <div ref={myRef} />
      {loading ? (
        <CircularProgress />
      ) : showEmptyState ? (
        <EmptyState
          title="Exam Not Available"
          message="The exam you're trying to access is not available or accessible at this time."
          redirectPath="/thebai/exams-available"
          buttonMessage="Back to Exams"
        />
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {loading ? (
                <CircularProgress />
              ) : (
                currentQuestion &&
                !currentQuestion.finished_exam && (
                  <CustomCard>
                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          position: "sticky",
                          top: 10,
                          right: 10,
                          float: "right",
                          zIndex: 1000,
                          alignSelf: "flex-end",
                        }}
                      >
                        <CountdownCircleTimer
                          isPlaying={isPlaying}
                          key={timerKey}
                          duration={timerDuration}
                          colors={["#004777", "#F7B801", "#A30000", "#A30000"]}
                          colorsTime={[timerDuration, timerDuration / 2, 10, 0]}
                          size={60}
                          strokeWidth={6}
                          onComplete={() => {
                            setOpenTimeoutCard(true);
                            handleQuestionTimeout();
                            return { shouldRepeat: false };
                          }}
                        >
                          {renderTimer}
                        </CountdownCircleTimer>
                      </div>
                      <Box
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Stack
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Title
                            value={`Question ${currentQuestion.question_number}/${currentQuestion.exam_length}`}
                            color={theme.palette.primary.main}
                          />
                          <Box
                            style={{
                              width: "50%",
                            }}
                          >
                            <ProgressBar
                              currentValue={currentQuestion.question_number}
                              totalValue={currentQuestion.exam_length}
                            />
                          </Box>
                          <Subtitle
                            value={questionType}
                            color={theme.palette.primary.main}
                          />
                        </Stack>
                      </Box>
                      <QuestionDetail
                        key={currentQuestion?.question?.id}
                        selectedOptions={selectedOptions}
                        onChange={selectOption}
                        language={currentQuestion.language || "python"}
                      />
                      <Box display={"flex"} justifyContent={"end"}>
                        <LoadingButton
                          loading={!isPlaying}
                          variant="contained"
                          style={{
                            backgroundColor: theme.palette.primary.main,
                            marginBottom: "4px",
                          }}
                          onClick={() => sendAnswer()}
                        >
                          {currentQuestion.question_number ===
                          currentQuestion.exam_length
                            ? "Finish Exam"
                            : currentQuestion.question.type === "coding"
                            ? "Submit Code & Continue"
                            : "Next Question"}
                        </LoadingButton>
                      </Box>
                    </div>
                  </CustomCard>
                )
              )}
            </Grid>
          </Grid>
          <div>
            <Dialog
              open={openFinalCard}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
              hideBackdrop={true}
            >
              <DialogTitle id="alert-dialog-title">
                <Typography variant="h6" component="h2">
                  {"Congratulations!"}
                </Typography>
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  {"You have successfully completed the exam"}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    routeChange("/thebai/exams-available");
                  }}
                  variant={"contained"}
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    marginLeft: "10px",
                    color: "white",
                  }}
                  size={"medium"}
                  autoFocus
                >
                  {"Continue"}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
          <div>
            <Dialog
              open={openNoOptionSelectedCard}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <DialogTitle id="alert-dialog-title">
                <Typography variant="h6" component="h2">
                  {currentQuestion?.question?.type === "coding"
                    ? "You haven not written any code"
                    : "You did not select any answer!"}
                </Typography>
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  {
                    "You didn't answer the question. Do you wish to continue to the next question?"
                  }
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setOpenNoOptionSelectedCard(false);
                  }}
                  variant={"outlined"}
                  size={"medium"}
                >
                  {"Take me back"}
                </Button>
                <Button
                  onClick={() => {
                    setOpenNoOptionSelectedCard(false);
                    answerQuestion();
                  }}
                  variant={"contained"}
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    marginLeft: "10px",
                    color: "white",
                  }}
                  size={"medium"}
                  autoFocus
                >
                  {"Continue"}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
          <div>
            <Dialog
              open={openCodeConfirmationModal}
              aria-labelledby="code-confirmation-title"
              aria-describedby="code-confirmation-description"
            >
              <DialogTitle id="code-confirmation-title">
                <Typography variant="h6" component="h2">
                  {"Run your code first?"}
                </Typography>
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="code-confirmation-description">
                  {
                    "You have written code but haven't run it yet. Would you like to test your code before continuing to the next question?"
                  }
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setOpenCodeConfirmationModal(false);
                  }}
                  variant={"outlined"}
                  size={"medium"}
                >
                  {"Run Code First"}
                </Button>
                <Button
                  onClick={handleConfirmSubmitCode}
                  variant={"contained"}
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    marginLeft: "10px",
                    color: "white",
                  }}
                  size={"medium"}
                  autoFocus
                >
                  {"Submit Code As-Is"}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </>
      )}
      {/* Keep outside the loading branch so continue-after-timeout can recover. */}
      <Dialog
        open={openTimeoutCard}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: "white",
            },
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">
          <Typography variant="h6" component="h2">
            {"Time is Up!"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {timeoutSubmitFailed
              ? "Something went wrong while continuing the exam. You can try again now, or come back later."
              : "What you last marked was sent as the answer. Do you wish to continue now or later?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenTimeoutCard(false);
              navigate("/thebai/exams-available");
            }}
            variant={"outlined"}
            size={"medium"}
            disabled={timeoutSubmitPending || continueNowPending}
          >
            {"Continue Later"}
          </Button>
          <Button
            onClick={handleContinueNowAfterTimeout}
            variant={"contained"}
            style={{
              backgroundColor: theme.palette.primary.main,
              marginLeft: "10px",
              color: "white",
            }}
            size={"medium"}
            autoFocus
            disabled={timeoutSubmitPending || continueNowPending}
          >
            {timeoutSubmitPending
              ? "Saving..."
              : continueNowPending
              ? "Continuing..."
              : "Continue Now"}
          </Button>
        </DialogActions>
      </Dialog>
    </MasterPage>
  );
};

export default ExamQuestion;
